/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import _ from 'lodash';

import StringCell from '@/backgrid/StringCell';
import OrderTypes from '@/enums/OrderTypes';

import OrderDeliveryView from '../views/OrderDeliveryView';
import GetAllRegions from '../../common/services/GetAllRegions';
import GetDeliveryScheduleColumnsForOrder from '../services/GetDeliveryScheduleColumnsForOrder';
import GetDeliverySchedulesByOrderId from '../services/GetDeliverySchedulesByOrderId';
import GetCommandPOCsByCommandID from '../../external-dashboard/services/GetCommandPOCsByCommandID';
import GetDeliveryScheduleInfoForMFDRelocationForm from '../services/GetDeliveryScheduleInfoForMFDRelocationForm';
import GetCustomerOrderDetails from '../services/GetCustomerOrderDetails';
import GetDeliveryScheduleImportByOrderId from '../services/GetDeliveryScheduleImportByOrderId';
import GetAllTrainingTypes from '../../common/services/GetAllTrainingTypes';
import GetMFDAdditionalServicesByObjectID from '../../common/services/GetMFDAdditionalServicesByObjectID';
import GetAdditionalItemsByOrderFormTypeID from '../../common/services/GetAdditionalItemsByOrderFormTypeID';
import GetCONUSAndOCONUSRegions from '../services/GetCONUSAndOCONUSRegions';
import OrderAccess from '../utils/OrderAccess';

export default async function loadOrderDeliveryView(orderDetails) {
  // await save promise
  const commandDetails = await GetCustomerOrderDetails(orderDetails.orderDetails.orderId);

  const commandName = commandDetails.commandName;
  const branchName = commandDetails.branchName;
  let scheduleService;
  if (orderDetails.orderTypeId === '12') {
    scheduleService = GetDeliveryScheduleInfoForMFDRelocationForm;
  } else {
    scheduleService = GetDeliverySchedulesByOrderId;
  }
  let additionalItemsService, data1, data2;
  if (orderDetails.orderTypeId === '1' || orderDetails.orderTypeId === '3') {
    //MFD
    additionalItemsService = GetMFDAdditionalServicesByObjectID;
    data1 = orderDetails.orderDetails.orderId;
    data2 = 'ORDER';
  } else {
    additionalItemsService = GetAdditionalItemsByOrderFormTypeID;
    data1 = orderDetails.orderTypeId;
  }
  const [schedules, regions, commandPOCs, trainingTypes, files, additionalItems, conusOconusRegions, prtnrA3Columns] =
    await Promise.all([
      scheduleService(orderDetails),
      GetAllRegions(),
      GetCommandPOCsByCommandID(orderDetails.orderDetails.commandId),
      GetAllTrainingTypes(),
      GetDeliveryScheduleImportByOrderId(orderDetails.orderId, orderDetails.orderDetails.commandId),
      additionalItemsService(data1, data2),
      GetCONUSAndOCONUSRegions(),
      GetDeliveryScheduleColumnsForOrder(orderDetails.orderId)
    ]);

  const columns = prtnrA3Columns.filter(({ name }) => name !== 'lineClin')
    .map(({ name, label, width, renderableBy, downloadableBy }) => ({
      name,
      label,
      cell: StringCell,
      headerCell: 'custom',
      width: parseInt(width),
      editable: false,
      downloadable: OrderAccess.isDeliveryScheduleColumnDownloadable(orderDetails.orderDetails.statusId, downloadableBy),
      renderable: OrderAccess.isDeliveryScheduleColumnRenderable(orderDetails.orderDetails.statusId, renderableBy)
    }));

  const isOtherCountry =
    (orderDetails.orderTypeId < '27' && orderDetails.orderTypeId > '21') || orderDetails.orderTypeId === '12';

  //filter through regions for US vs INTL orders
  //getAllregions returns true/false values for isEuropeMiddleEast, but 0/1 for all other boolean columns
  let filteredRegions;
  if (orderDetails.orderTypeId === '22') {
    //japan
    filteredRegions = regions?.filter((region) => region.isJapan === '1');
  } else if (orderDetails.orderTypeId === '23') {
    filteredRegions = regions?.filter((region) => region.isRegion1 === 'true');
  } else if (orderDetails.orderTypeId === '24') {
    filteredRegions = regions?.filter((region) => region.isRegion2 === 'true');
  } else if (orderDetails.orderTypeId === '25') {
    filteredRegions = regions?.filter((region) => region.isRegion3 === 'true');
  } else if (orderDetails.orderTypeId === '26') {
    filteredRegions = regions?.filter((region) => region.isRegion4 === 'true');
  } else if (orderDetails.orderTypeId === '21')
    //AK/HI/PR + Other territories
    filteredRegions = regions?.filter(
      (region) =>
        region.isCONUS === '0' &&
        region.isMFD === '1' &&
        region.isPRTNR === '1' &&
        region.isRelocation === '1' &&
        region.isMaintenance === '1' &&
        region.isJapan === '0'
    );
  else {
    filteredRegions = regions?.filter((region) => region.isEuropeMiddleEast === 'false' && region.isJapan === '0');
  }

  const isReplacementOptions = [
    { id: 0, name: 'New' },
    { id: 1, name: 'Replacement' }
  ];

  const isMFD = orderDetails.orderTypeId === '1' || orderDetails.orderTypeId === '3' ? true : false;

  let fromDevice;
  let toDevice;

  if (orderDetails.orderTypeId === '12') {
    const schedule = schedules[0];
    schedule.equipmentName = schedule?.deviceMake?.concat(' ', schedule?.deviceModel);
    fromDevice = Object.assign({}, schedule);
    toDevice = Object.assign({}, schedule);

    fromDevice.deliveryPOCName = schedule.fromDeliveryPOCName;
    fromDevice.deliveryPOCFirstName = schedule.fromDeliveryPOC?.deliveryPOCFirstName;
    fromDevice.deliveryPOCLastName = schedule.fromDeliveryPOC?.deliveryPOCLastName;
    fromDevice.deliveryPOCEmail = schedule.fromDeliveryPOC?.deliveryPOCEmail;
    fromDevice.deliveryPOCPhone = schedule.fromDeliveryPOC?.deliveryPOCPhone;

    fromDevice.itPOCName = schedule.fromITPOCName;
    fromDevice.itPOCFirstName = schedule.fromITPOC?.itPOCFirstName;
    fromDevice.itPOCLastName = schedule.fromITPOC?.itPOCLastName;
    fromDevice.itPOCEmail = schedule.fromITPOC?.itPOCEmail;
    fromDevice.itPOCPhone = schedule.fromITPOC?.itPOCPhone;

    fromDevice.organization = schedule.fromOrgUnit;
    fromDevice.base = schedule.fromBase;

    toDevice.deliveryPOCName = schedule.toDeliveryPOCName;
    toDevice.deliveryPOCFirstName = schedule.toDeliveryPOC?.deliveryPOCFirstName;
    toDevice.deliveryPOCLastName = schedule.toDeliveryPOC?.deliveryPOCLastName;
    toDevice.deliveryPOCEmail = schedule.toDeliveryPOC?.deliveryPOCEmail;
    toDevice.deliveryPOCPhone = schedule.toDeliveryPOC?.deliveryPOCPhone;

    toDevice.itPOCName = schedule.toITPOCName;
    toDevice.itPOCFirstName = schedule.toITPOC?.itPOCFirstName;
    toDevice.itPOCLastName = schedule.toITPOC?.itPOCLastName;
    toDevice.itPOCEmail = schedule.toITPOC?.itPOCEmail;
    toDevice.itPOCPhone = schedule.toITPOC?.itPOCPhone;

    toDevice.organization = schedule.toOrgUnit;
    toDevice.base = schedule.toBase;

    fromDevice.address = schedule.fromAddress;
    toDevice.address = schedule.toAddress;
  } else {
    _.each(schedules, (result, i) => {
      if (result.device?.isColor === '1') {
        result.device.colorText = 'C';
      } else if (result.device?.isColor === '0') {
        result.device.colorText = 'M';
      }
      result.colorText = result.device.colorText;
      if (result.device?.isNipr === '1') {
        result.device.niprText = 'NIPR';
      } else if (result.device?.isNipr === '0') {
        result.device.niprText = 'SIPR';
      }
      result.niprText = result.device.niprText;
      result.isReplacementText =
        result.isReplacement === '0' ? 'New' : result.isReplacement === '1' ? 'Replacement' : '';
      result.trainingTypeText = result.trainingType;
      result.additionalServiceText = result.additionalService ? result.additionalService : result.mfdAdditionalService;
      result.additionalAccessoryText = result.additionalAccessory ? result.additionalAccessory : null;
      result.clin = result.device?.clin;
      result.lineNumber = result.device?.lineNumber;
      result.customDescription = result.device?.customDescription;
      result.formattedPrice = parseInt(result.device?.price).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });

      if (result.device.description) {
        result.equipmentName = result.device.description;
      } else if (result.device.volumeBand) {
        result.equipmentName = result.device.volumeBand.concat(result.device.niprText, result.device.colorText);
        result.volumeBand = result.device.volumeBand;
        if (result.device.optionalAccessories) {
          result.equipmentName = result.equipmentName.concat('  -  ', result.device.optionalAccessories);
          result.optionalAccessories = result.device.optionalAccessories;
        }
      } else {
        result.equipmentName = '';
      }
      //vb0
      if (orderDetails.orderTypeId === '16' || orderDetails.orderTypeId === '18') {
        result.device.colorText = result.device?.isColor === '1' ? 'Color' : 'Monochrome (B&W)';
        result.device.niprText = result.device?.isNipr === '1' ? 'NIPR' : 'SIPR';
        if (result.device.volumeBand) {
          result.equipmentName = result.device.niprText.concat(
            ' ',
            result.device.volumeBand,
            ' ',
            result.device.colorText,
            ' with ',
            result.device.warrantyYear,
            ' year(s) warranty'
          );
        } else if (result.device.description) {
          result.equipmentName = result.device.description;
        } else {
          result.equipmentName = '';
        }
      }
      result.commandName = commandName;
      result.branchName = branchName;
      setAddress(result);
      setEquipmentName(result);
    });
  }

  function setAddress(schedule) {
    schedule.addressLine1 = schedule.address.addressLine1;
    schedule.addressLine2 = schedule.address.addressLine2;
    schedule.streetAddress = schedule?.addressLine1?.concat(', ', schedule?.addressLine2);
    schedule.buildingFloorNumber = schedule.address.buildingFloorNumber;
    schedule.city = schedule.address.city;
    schedule.regionCode = schedule.address.regionCode;
    schedule.roomNumber = schedule.address.roomNumber;
    schedule.poleNumber = schedule.address.poleNumber;
    schedule.suite = schedule.address.suite;
    schedule.roomPoleNumber = schedule.address.roomPoleNumber;
    schedule.floorNumber = schedule.address.floorNumber;
    schedule.zip = schedule.address.zip;
    if (schedule.zip) {
      schedule.address.isConus = '1';
    }
  }

  function setEquipmentName(schedule) {
    if (orderDetails.orderTypeId === OrderTypes.PRTNR_A3) {
      schedule.equipmentName = schedule.device.equipmentName;
      return;
    }

    if (schedule.device.model) {
      schedule.equipmentName = schedule.device.model;
    } else if (schedule.device.laserPrinterTonerCartridgeType) {
      schedule.equipmentName = schedule.device.laserPrinterTonerCartridgeType;
    } else if (schedule.device.configurationAndPricing) {
      schedule.equipmentName = schedule.device.volumeBand.concat('  -  ', schedule.device.configurationAndPricing);
    } else if (schedule.device.description) {
      schedule.equipmentName = schedule.device.description;
    } else if (schedule.device.volumeBand) {
      schedule.equipmentName = schedule.device.volumeBand.concat(schedule.device.niprText, schedule.device.colorText);
      if (schedule.device.optionalAccessories) {
        schedule.equipmentName = schedule.equipmentName.concat('  -  ', schedule.device.optionalAccessories);
      }
    } else {
      schedule.equipmentName = '';
    }
  }

  const itPOCs = [];
  const allPOCs = [];
  _.each(commandPOCs, (POC) => {
    if (POC.type === 'IT') {
      itPOCs.push(POC);
    }
    allPOCs.push(POC);
  });

  const loadModel = new Backbone.Model({
    orderDetails,
    columns,
    schedules,
    isReadOnly: orderDetails.isReadOnly,
    regions: filteredRegions?.filter((region) => region?.isSpecialty === 'false'),
    conusOconusRegions,
    fromDevice,
    toDevice,
    files,
    commandPOCs: {
      itPOCs,
      allPOCs
    },
    trainingTypes,
    isReplacementOptions,
    isOtherCountry,
    additionalServices: additionalItems.additionalServices,
    additionalAccessories: additionalItems.additionalAccessories,
    isMFD,
    showCSV: true
  });

  return new OrderDeliveryView({
    model: loadModel
  });
}
